<template>
	<gibs-breadcrumb v-if="useBreadCrumb"></gibs-breadcrumb>
	<main>
		<ContentRenderer :value="transformedData">
			<ContentRendererMarkdown :value="transformedData || {}" v-if="transformedData" />
		</ContentRenderer>
		<gibs-social-links v-if="isNews && transformedData" ></gibs-social-links>
	</main>
</template>

<script setup lang="ts">
import { contentShowAndHide, contentExpandAndCollapse, updateRelativeUrl, updateHead  } from '~/utils/html-utils';

const props = defineProps({
	currentUrl: {
		type: String,
		default: ""
	}
});

// SEO attrs
let title = ref('');
let seoTitle = ref('');
let metaKeywords = ref('');
let metaDesc = ref('');
let seoType = ref();
let seoBrochureURL = ref();
let seoAuthor = ref();
let ogImage = ref('');
let seoPublishedTime = ref();
let isNews = ref(false);
let useBreadCrumb = ref(false);

// Create a ref to hold the transformed content data
const transformedData = ref<Record<string, any> | undefined>(undefined);
const route = useRoute();
const slug = Array.isArray(route.params.slug)
	? route.params.slug
	: [...route.params.slug];

const config = useRuntimeConfig();
const blobStorageBasePath = `${config.public.imgUri}/${config.public.contentName}`;
const pageName = slug[slug.length - 1];

// news or about content?
let pageUrl = `/pages/${pageName}.json`; //${blobStorageBasePath}

if (slug[0].toLowerCase() === 'news') {
	isNews.value = true;
	useBreadCrumb.value = true;
	const newsSlug = 'news-listings/articles/' + pageName;
	pageUrl = `/${newsSlug}.json`; //${blobStorageBasePath}
}

//$fetch(pageUrl)
fetchJsonData(pageUrl).then(async (data : any) =>
{
	// TODO: Insert SEO headers into markdown
	updateHead({
            title: data.SEOTitle ?? data.Name,
            keywords: data.MetaKeywords,
            description: data.MetaDesc,
            ogTitle: data.SEOTitle ?? data.Name,
            ogDescription: data.MetaDesc,
            ogUrl: "",
            ogImage: data.seoImage, 
            ogType: data.seoType,
            articleAuthor: data.seoAuthor,
            articlePublishedTime: data.seoPublishedTime,
            hid: '',
            brochureUrl: data.seoBrochureURL 
        });
	
// Please take note that their should not be a space between the # and the content, ${data.Content} should be the first char on the next line
	let markDown = '#'
	markDown += '\r\n'
	markDown += `${data.Content}`;
	
	if (markDown !== null){
		transformedData.value = await parseMarkdown(markDown);
	}
}).catch(async (error : any) => {
	if(process.client) {
		let redirectToCheck = window.location.pathname;
		if(redirectToCheck.indexOf('/') == 0)
			redirectToCheck = redirectToCheck.substring(1).toLowerCase();
		let redirectValue = await getRedirects(redirectToCheck);

		if (!redirectValue || redirectValue === '' || redirectValue.startsWith("http") || redirectValue.startsWith("www")) {
			await $fetch('/api/logRequest/logRequest', 
				{
					method: "POST",
					body : {
					path: window.location.href,
					status : '404'
					}
				});

			const message = `The page ${slug.join('/')} was not found.`;
			return navigateTo({ path: '/404', query: { message: message } });
		}
		else
		{
			if (redirectValue.startsWith('/media')){
				return navigateTo(`${blobStorageBasePath}/${redirectValue}`, {external: true});
			}
			else{
				return	navigateTo(redirectValue);
			}
		}
	}
});

async function getRedirects(redirectKey: string): Promise<string> {
	let redirectvalue: string;
	let response = await fetchJsonData('/redirects');
	let dictionary = JSON.parse(JSON.stringify(response, null, 2).toLowerCase());
	redirectvalue = dictionary[redirectKey];

	return redirectvalue;
}

const contentShowHideElements = ref();
const addedSeeMoreLessElements = ref<boolean>(false);

watch(transformedData, () => {
	// add a 3 second delay so that the dom has a chance to get loaded
	setTimeout(() => {
		contentShowHideElements.value = document.querySelectorAll(".gibs-show-hide-initiate");
		if (contentShowHideElements.value.length > 0 && addedSeeMoreLessElements.value === false) {
			contentShowHideElements.value.forEach((element: HTMLElement, index: number) => {
			// targetElement by target-id
			const targetId = element.getAttribute("data-target-id");
			const targetElement = document.getElementById(targetId!);
			//console.log(targetElement);
			targetElement?.classList.add("see-less");

			// create button element
			const seeMoreButtonElement = document.createElement("button");
			// add class to button element
			seeMoreButtonElement.classList.add("gibs-text-link", "see-more-less-button", `see-more-less-button-${index}`);
			// add button text
			const node = document.createTextNode("See More");
			seeMoreButtonElement.appendChild(node);
			seeMoreButtonElement.classList.add("see-more");
			// add up and down icons to button (tailwind styling to toggle between the icons)
			const upIcon = document.createElement("img");

			upIcon.src = updateRelativeUrl("/media/3xkdk0cb/up-actionicons.svg");
			upIcon.classList.add("see-less-icon");
			const upIconHover = document.createElement("img");
			upIconHover.src = updateRelativeUrl("/media/pw5ndkrt/up-actionicons-hover.svg");
			upIconHover.classList.add("see-less-icon-hover");

			const downIcon = document.createElement("img");
			downIcon.src = updateRelativeUrl("/media/uotb0btq/down-actionicons.svg");
			downIcon.classList.add("see-more-icon");
			const downIconHover = document.createElement("img");
			downIconHover.src = updateRelativeUrl("/media/zjdfkmcz/down-actionicons-hover.svg");
			downIconHover.classList.add("see-more-icon-hover");
			seeMoreButtonElement.appendChild(upIcon);
			seeMoreButtonElement.appendChild(upIconHover);
			seeMoreButtonElement.appendChild(downIcon);
			seeMoreButtonElement.appendChild(downIconHover);

			element.appendChild(seeMoreButtonElement);

			addedSeeMoreLessElements.value = true;
			seeMoreButtonElement.addEventListener("click", () => {
				contentShowAndHide(seeMoreButtonElement, targetElement!);
			});
			});
		}

		initExpandAndCollapse();
 	}, 3000); // 3000 milliseconds = 3 seconds
});

</script>